import React from "react";
import { Route } from "react-router-dom";

// Site Émilie (démo) — rendu via le système de modèles
import { TenantProvider, useTenant } from "@/lib/TenantContext";
import { useBrandingTheme } from "@/lib/useBrandingTheme";
import SiteTemplateRenderer from "@/templates/SiteTemplateRenderer";

// Pages publiques (démo Émilie)
import Reserver from "@/public/Reserver";
import ConsultBooking from "@/public/ConsultBooking";
import ConsultRoom from "@/public/ConsultRoom";
import Boutique from "@/public/Boutique";
// crm-module BEGIN C2.03 (lane F2) — public session-pack purchase
import PackPurchase from "@/public/packs/PackPurchase";
// crm-module END
import Programmes from "@/public/Programmes";
import Parcours from "@/public/Parcours";
import PathwayMember from "@/public/PathwayMember";
import PathwayStep from "@/public/pathways/PathwayStep";
import NudgesOff from "@/public/pathways/NudgesOff";
import JournalIndex from "@/public/JournalIndex";
import JournalPost from "@/public/JournalPost";
import CheckoutSuccess from "@/public/CheckoutSuccess";
import ClientPortal from "@/public/ClientPortal";
import ManageBooking from "@/public/ManageBooking";
import LearnPlayer from "@/public/LearnPlayer";
import LearnCatalogue from "@/public/LearnCatalogue";
import MarketplaceLearn from "@/public/MarketplaceLearn";
import MarketplacePractitioner from "@/public/MarketplacePractitioner";
import ReviewForm from "@/public/ReviewForm";
// crm-module BEGIN C2.06 (lane F1) — reminder Confirm + marketing-footer Unsubscribe
import BookingConfirm from "@/public/crm/BookingConfirm";
import EmailUnsubscribe from "@/public/crm/EmailUnsubscribe";
// crm-module END

// SaaS / plateforme Solunary
import PlatformLanding from "@/saas/PlatformLanding";
import Signup from "@/saas/Signup";
import LegalPage from "@/saas/LegalPage";
import TermsAndConditions from "@/saas/TermsAndConditions";
import SupportPage from "@/saas/SupportPage";
import SubscribeSuccess from "@/saas/SubscribeSuccess";
import AuthBridge from "@/saas/AuthBridge";
import { SolunaryBlog, SolunaryBlogPost } from "@/saas/SolunaryBlog";
import { SolunaryCare } from "@/saas/SolunaryP3";

import ArchitecturePage from "@/architecture/ArchitecturePage";

const EmilieInner = () => {
  const { tenant, loading } = useTenant();
  useBrandingTheme(tenant?.branding);
  if (loading || !tenant) return <div data-testid="emilie-landing-page" className="min-h-screen bg-eh-bg" />;
  return (
    <div data-testid="emilie-landing-page">
      <SiteTemplateRenderer tenant={tenant} />
    </div>
  );
};

const EmilieLanding = () => (
  <TenantProvider slug="emilie">
    <EmilieInner />
  </TenantProvider>
);

export const platformRoutes = (
  <>
    {/* === Solunary platform = home par défaut === */}
    <Route path="/" element={<PlatformLanding />} />
    <Route path="/platform" element={<PlatformLanding />} />
    <Route path="/platform/signup" element={<Signup />} />
    <Route path="/legal/:doc" element={<LegalPage />} />
    <Route path="/terms" element={<TermsAndConditions />} />
    <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
    <Route path="/terms-of-service" element={<TermsAndConditions />} />
    <Route path="/cgu" element={<TermsAndConditions />} />
    <Route path="/cgv" element={<TermsAndConditions />} />
    <Route path="/privacy" element={<LegalPage />} />
    <Route path="/contact" element={<SupportPage />} />
    <Route path="/support" element={<SupportPage />} />
    <Route path="/help" element={<SupportPage />} />
    <Route path="/platform/subscribe/success" element={<SubscribeSuccess />} />
    <Route path="/auth/bridge" element={<AuthBridge />} />
    <Route path="/blog" element={<SolunaryBlog />} />
    <Route path="/blog/:slug" element={<SolunaryBlogPost />} />
    <Route path="/care" element={<SolunaryCare />} />
    <Route path="/learn" element={<MarketplaceLearn />} />
    <Route path="/marketplace" element={<MarketplaceLearn />} />
    {/* AFTER the exact "/marketplace" above: react-router ranks static segments over
        dynamic ones, so the order is documentation rather than mechanism — but a reader
        moving one of the two lines should see which one is the index. */}
    <Route path="/marketplace/:slug" element={<MarketplacePractitioner />} />
    <Route path="/learn/:courseId" element={<LearnPlayer />} />

    {/* === Démo : site Émilie (anciennement à la racine) === */}
    <Route path="/demo" element={<EmilieLanding />} />
    <Route path="/reserver" element={<Reserver />} />
    {/* Flow C (per-minute telehealth) — the disclosure + consent step before the Stripe
        Checkout redirect. Registered on BOTH trees, here and in tenant/TenantSite.jsx,
        same "missing on one host renders blank" rule as every other public page. */}
    <Route path="/consultation/confirmer" element={<ConsultBooking />} />
    {/* The patient's standing room page (BUG #3 fix) — `ConsultJoinButton`'s
        navigation target every time a consult is re-entered outside the checkout
        redirect (which lands on `/merci/consultation` instead, via CheckoutSuccess). */}
    <Route path="/consultation/salle" element={<ConsultRoom />} />
    <Route path="/boutique" element={<Boutique />} />
    {/* crm-module BEGIN C2.03 (lane F2) — public session-pack purchase, same
        both-trees registration rule as every other public page here. */}
    <Route path="/forfaits" element={<PackPurchase />} />
    {/* crm-module END */}
    <Route path="/programmes" element={<Programmes />} />
    {/* Pathways (contract §9) — registered on BOTH trees (here and tenant/TenantSite.jsx):
        the nav links /parcours whenever the practice has an active pathway, and the
        pathway page is where the portal and every pathway mail send a learner. The static
        `nudges-off` segment outranks `:pathwayId`. */}
    <Route path="/parcours" element={<Parcours />} />
    <Route path="/parcours/nudges-off" element={<NudgesOff />} />
    <Route path="/parcours/:pathwayId" element={<PathwayMember />} />
    <Route path="/parcours/:pathwayId/etape/:stepId" element={<PathwayStep />} />
    <Route path="/journal" element={<JournalIndex />} />
    <Route path="/journal/:slug" element={<JournalPost />} />
    <Route path="/compte" element={<ClientPortal />} />
    <Route path="/gerer-rdv" element={<ManageBooking />} />
    {/* The review form the post-session mail links to. Registered on BOTH trees, here and
        in tenant/TenantSite.jsx, exactly like "/gerer-rdv": the link is built against the
        PRACTICE's origin, and a page missing from one host renders blank there. */}
    <Route path="/avis" element={<ReviewForm />} />
    {/* crm-module BEGIN C2.06 (lane F1) — reminder Confirm button + marketing-footer
        Unsubscribe. Registered on BOTH trees, here and in tenant/TenantSite.jsx, exactly
        like "/gerer-rdv" and "/avis": the link is mailed under the practitioner's brand. */}
    <Route path="/rdv/confirmer" element={<BookingConfirm />} />
    <Route path="/desabonnement" element={<EmailUnsubscribe />} />
    {/* crm-module END */}
    <Route path="/t/:tenantSlug/learn" element={<LearnCatalogue />} />
    <Route path="/t/:tenantSlug/learn/:courseId" element={<LearnPlayer />} />
    <Route path="/merci/:kind" element={<CheckoutSuccess />} />
    
    <Route path="/~/architecture" element={<ArchitecturePage />} />
    <Route path="/-/architecture" element={<ArchitecturePage />} />
  </>
);
